import { useState } from "react";
import { ArrowRight, Phone, MapPin, Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/site/PageHeader";
import { api, WHATSAPP_LINK, PHONE_DISPLAY, CLUB_LOCATION, CLUB_PRESIDENT } from "../lib/api";

const initial = { full_name: "", phone: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Mesajınız iletildi!");
    } catch {
      toast.error("Mesaj gönderilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="İletişim"
        title="Bize Ulaş"
        subtitle="Soruların, kayıt taleplerin, iş birlikleri ve görüşlerin için bizimle iletişime geçebilirsin."
      />

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-5 space-y-4">
            <ContactCard icon={MapPin} label="Adres" value={CLUB_LOCATION} hint="Tesis adresi yakında güncellenecektir." />
            <ContactCard icon={Phone} label="Telefon" value={PHONE_DISPLAY} link="tel:+905078664360" />
            <ContactCard icon={Mail} label="E-posta" value="info@focussporkulubu.com" link="mailto:info@focussporkulubu.com" />
            <ContactCard icon={MessageCircle} label="WhatsApp" value="Anlık iletişim" link={WHATSAPP_LINK} external />
            <div className="card-elev p-6">
              <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground mb-1">Kulüp Başkanı</div>
              <div className="font-display text-xl font-bold uppercase tracking-wider">{CLUB_PRESIDENT}</div>
            </div>
          </aside>

          <div className="lg:col-span-7">
            {success ? (
              <div className="card-elev p-10 text-center" data-testid="contact-success">
                <div className="h-16 w-16 mx-auto rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-400 mb-5">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h2 className="heading-md">Mesajınız İletildi</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                  En kısa sürede size geri dönüş yapacağız. Acil durumlarda WhatsApp üzerinden ulaşabilirsiniz.
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary mt-6">
                  <MessageCircle className="h-4 w-4" /> WhatsApp'a Yönlendir
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="card-elev p-6 md:p-8" data-testid="contact-form">
                <div className="eyebrow mb-3">İletişim Formu</div>
                <h2 className="heading-md mb-6">Bize Yaz</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Ad Soyad" required>
                    <input className="input-base" required value={form.full_name} onChange={update("full_name")} data-testid="contact-name" />
                  </Field>
                  <Field label="Telefon" required>
                    <input className="input-base" required value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
                  </Field>
                  <Field label="E-posta" required>
                    <input type="email" className="input-base" required value={form.email} onChange={update("email")} data-testid="contact-email" />
                  </Field>
                  <Field label="Konu" required>
                    <input className="input-base" required value={form.subject} onChange={update("subject")} data-testid="contact-subject" />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Mesaj" required>
                    <textarea className="input-base min-h-[140px]" required value={form.message} onChange={update("message")} data-testid="contact-message" />
                  </Field>
                </div>
                <button type="submit" disabled={submitting} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="contact-submit">
                  {submitting ? "Gönderiliyor..." : "Mesajı Gönder"} {!submitting && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-input">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}

function ContactCard({ icon: Icon, label, value, hint, link, external }) {
  const Body = (
    <>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-primary shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
          <div className="font-display font-bold uppercase tracking-wider">{value}</div>
          {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
      </div>
    </>
  );
  if (link) {
    return (
      <a href={link} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className="card-elev p-5 block" data-testid={`contact-card-${label}`}>
        {Body}
      </a>
    );
  }
  return <div className="card-elev p-5" data-testid={`contact-card-${label}`}>{Body}</div>;
}
